import React, { useEffect } from 'react';
import { Button, Col, Row } from 'reactstrap';
import UploadImage from '../assets/img/upload.svg';
import { Image } from 'react-bootstrap';
import '../assets/style/MediaThumbnailUpload.css';
import Divider from '@mui/material/Divider';
import MTextField from './MTextField';
import CloseIcon from '@mui/icons-material/Close';
import classNames from 'classnames';
import { mediaForTypes } from '../utils/Constant';
import { handleThumbnailUploadFiles } from '../pages/admin-settings/knowledgeCenter/knowledgeCenterHelper';

interface IMediaThumbnailUploadPropTypes {
  thumbnailSupportedFiles?: string[];
  thumbnailMaxSize?: number;
  mediaMaxSize?: number;
  mediaSupportedFiles?: string[];
  data: any;
  setData: (arg: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  disabled?: boolean;
}

const MediaThumbnailUpload: React.FunctionComponent<
  IMediaThumbnailUploadPropTypes
> = ({
  mediaSupportedFiles = ['.mp4', '.pdf'],
  thumbnailSupportedFiles = ['.png', '.jpeg', '.jpg'],
  data,
  setData,
  onSubmit,
  onCancel,
  thumbnailMaxSize = 1024 * 500,
  mediaMaxSize = 1024 * 1024 * 50,
  disabled,
}) => {
  const thumbnailInputRef = React.useRef<HTMLInputElement>(null);
  const mediaInputRef = React.useRef<HTMLInputElement>(null);
  const handleDrop = (
    event: React.DragEvent<HTMLButtonElement>,
    mediaFor: string
  ) => {
    event.preventDefault();
    handleThumbnailUploadFiles(
      Array.from(event.dataTransfer.files || []),
      mediaFor,
      thumbnailSupportedFiles,
      mediaSupportedFiles,
      thumbnailMaxSize,
      mediaMaxSize,
      data,
      setData
    );
  };

  const handleBrowseFiles = (
    event: React.ChangeEvent<HTMLInputElement>,
    mediaFor: string
  ) => {
    handleThumbnailUploadFiles(
      Array.from(event.target.files || []),
      mediaFor,
      thumbnailSupportedFiles,
      mediaSupportedFiles,
      thumbnailMaxSize,
      mediaMaxSize,
      data,
      setData
    );
  };

  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const removeFile = (mediaFor: string) => {
    setData({
      ...data,
      [mediaFor === mediaForTypes.thumbnail ? 'thumbnail' : 'media']: undefined,
    });
  };

  useEffect(() => {
    if (!data?.thumbnail) {
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
    }
    if (!data?.media) {
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    }
  }, [data?.thumbnail, data?.media]);

  const getDragDropZone = (
    title: string,
    mediaFor: string,
    supportedFiles: string[],
    file: File | undefined,
    fileInputRef: React.RefObject<HTMLInputElement>
  ) => (
    <>
      <div className="p-3 ps-0">
        <span className="upload-title">{title}</span>
        <span className="text-danger">{` *`}</span>
        <br />
      </div>
      <div className="pe-3">
        <button
          className="d-flex align-items-center flex-column drag-container button-container"
          tabIndex={0}
          onDrop={(e) => {
            handleDrop(e, mediaFor);
          }}
          onDragOver={handleDragOver}
        >
          <div>
            <Image src={UploadImage} width={50} height={50} />
          </div>
          <p className="upload-title mt-2 mb-1">
            Drag your file(s) to start uploading
          </p>
          <div className="m-1 w-50">
            <Divider>OR</Divider>
          </div>
          <div>
            <Button
              variant="outline-primary"
              className="mx-2 cl-btn-outline-primary"
              onClick={() => fileInputRef.current?.click()}
            >
              Browse Files
            </Button>
            <input
              aria-label="upload"
              type="file"
              ref={fileInputRef}
              className="display-none"
              accept={supportedFiles.join(',')}
              onChange={(e) => {
                handleBrowseFiles(e, mediaFor);
              }}
            />
          </div>
        </button>
      </div>
      <div className="p-2 subtitle pb-0">
        {`Only supports ${supportedFiles.join(', ')} files`}
      </div>
      <div className="p-2 subtitle pb-1 pt-1">
        {`Max file size: ${
          mediaFor === mediaForTypes.thumbnail
            ? `${thumbnailMaxSize / 1024} KB`
            : `${mediaMaxSize / (1024 * 1024)} MB`
        }`}
      </div>

      {file && (
        <Row>
          <Col xs={12} key={`uploading-files-${mediaFor}`} className="pt-3">
            <div className="position-relative pe-2 uploading-files-card ">
              <CloseIcon
                className="uploading-file-remove-icon"
                onClick={() => removeFile(mediaFor)}
              />
              <div className="file-details-container flex-grow-1">
                <div className="d-flex justify-content-between align-items-center pe-3">
                  <p className="file-name mb-0">
                    <small>{file.name}</small>
                  </p>
                </div>
                <p className="file-size mb-0">
                  {mediaFor === mediaForTypes.thumbnail ? (
                    <small>{`Size: ${(file.size / 1024).toFixed(2)} KB`}</small>
                  ) : (
                    <small>{`Size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`}</small>
                  )}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </>
  );

  return (
    <div
      className={classNames('media-upload-main-container', {
        disabled: disabled,
      })}
    >
      <div className="media-upload-container">
        <Row className="p-3">
          <Col md={12} lg={6}>
            <MTextField
              label="Title"
              value={data?.title ?? ''}
              onChange={(event) => {
                const value = event.target.value.trimStart();
                if (value || value == '') setData({ ...data, title: value });
              }}
              required
            />
          </Col>
          <Col md={12} lg={6}>
            <MTextField
              label="Order"
              value={data?.order ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                if (value === '' || /^[0-9]+$/.test(value)) {
                  setData({ ...data, order: value });
                }
              }}
              required
            />
          </Col>
          <Col md={12} lg={6}>
            {getDragDropZone(
              'Thumbnail Upload',
              mediaForTypes.thumbnail,
              thumbnailSupportedFiles,
              data?.thumbnail,
              thumbnailInputRef
            )}
          </Col>
          <Col md={12} lg={6}>
            {getDragDropZone(
              'Media Upload',
              mediaForTypes.media,
              mediaSupportedFiles,
              data?.media,
              mediaInputRef
            )}
          </Col>
        </Row>

        <div className="media-upload-footer p-2">
          <Button
            variant="primary"
            className="cl-btn-primary"
            onClick={onSubmit}
          >
            Submit
          </Button>
          <Button
            variant="outline-primary"
            className="mx-2 cl-btn-outline-primary"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MediaThumbnailUpload;
